import React, { createContext, ReactNode, useState } from "react";
import TabBar from "components/shared/TabBar";
import PageTitle from "components/layout/PageTitle";
import ExternalInviteForm from "components/administration/invites/ExternalInviteForm";
import BatchExternalInviteForm from "components/administration/invites/BatchExternalInviteForm";
import { useMutation, useQuery } from "react-query";
import {
  ExternalInviteArgs,
  Settings,
} from "components/administration/invites/types";
import { useNavigate, useParams } from "helpers/tixxt-router";
import { get } from "lodash";
import { fetchApi } from "helpers/reactQueryApi";
import { Errors, State } from "../../../@types";
import BatchMembershipInviteForm from "components/administration/invites/BatchMembershipInviteForm";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft as faRegularArrowLeft } from "@fortawesome/pro-regular-svg-icons";
import getContextUrl from "helpers/getContextUrl";
import { useSelector } from "react-redux";
import { groupCategorySelector, groupSelector } from "selectors/environment";

export const NewInviteContext = createContext<object | null>(null);

export default function NewInvitePage() {
  const navigate = useNavigate();
  const { groupSlug, groupCategoryId } = useParams();
  const group = useSelector((state: State) => groupSelector(state, groupSlug));
  const groupCategory = useSelector((state: State) =>
    groupCategorySelector(state, groupCategoryId),
  );
  const [selectedTabIndex, selectTabIndex] = useState(0);
  const [errors, setErrors] = useState<Errors | null>(null);
  const { data: settings } = useQuery<Settings>("/external_invites/settings");
  const context = {
    ...(group || groupCategory || Preload.current_network),
    type: group ? "group" : groupCategory ? "group_category" : "network",
  };
  const contextUrl = getContextUrl(context);

  const { mutate: inviteExternal } = useMutation(
    (
      body:
        | {
            external_invite: {
              email: string;
              first_name: string;
              last_name: string;
              subject: string;
              body: string;
              user_type?: "normal" | "external";
              group_ids?: string[];
              channel_ids?: string[];
            };
          }
        | FormData,
    ) =>
      fetchApi(`${contextUrl}/external_invites`, {
        method: "POST",
        body,
        headers:
          body instanceof FormData
            ? { ["Content-Type"]: "multipart/form-data" }
            : undefined,
      }),
    {
      onSuccess: ({ message }: { message: string }) => {
        navigate(`${contextUrl}/administration/invites/sent`);
        toastr.success(message);
      },
      onError: (error: { message: string }) => {
        setErrors(error["responseJson"]);
      },
    },
  );

  const tabs: {
    label: string;
    TabContent: (props: ExternalInviteArgs) => ReactNode;
  }[] = [];

  if (get(context, ["can", "invite_internal"]))
    tabs.push({
      label: I18n.t("js.invites.tabs.intern"),
      TabContent: BatchMembershipInviteForm,
    });

  if (
    get(context, ["can", "invite_external"]) ||
    get(context, ["can", "invite_external_guests"])
  ) {
    tabs.push({
      label: I18n.t("js.invites.tabs.email"),
      TabContent: ExternalInviteForm,
    });

    tabs.push({
      label: I18n.t("js.invites.tabs.email_list"),
      TabContent: BatchExternalInviteForm,
    });
  }

  const { TabContent } = tabs[selectedTabIndex];
  const defaultFormValues = {
    subject:
      settings?.default_invite_subject || I18n.t("js.invites.subject_default"),
    body:
      settings?.default_invite_text ||
      I18n.t(
        context.type === "network" || context.type === "group_category"
          ? "js.invites.message_body_default"
          : "js.invites.group_message_body_default",
        {
          author_name: Tixxt.currentMember.get("name"),
        },
      ),
    user_type: get(context, ["can", "invite_external_guests"])
      ? get(context, ["can", "invite_external"])
        ? "normal"
        : "external"
      : "",
  };

  return (
    <NewInviteContext.Provider value={context}>
      <PageTitle title={I18n.t("js.invites.new.title")} />
      <div className="btn-toolbar mb-10">
        <button
          className="btn btn-light btn-sm"
          onClick={(e) => {
            e.preventDefault();
            navigate(
              context.type === "group_category"
                ? `${contextUrl}/administration/directory`
                : `${contextUrl}/administration/invites/sent`,
            );
          }}
        >
          <FontAwesomeIcon className="text-primary" icon={faRegularArrowLeft} />
        </button>
      </div>
      <TabBar
        tabs={tabs}
        selectTabIndex={selectTabIndex}
        selectedTabIndex={selectedTabIndex}
        className={"mb-2"}
      />
      {TabContent && (
        <TabContent
          inviteExternal={inviteExternal}
          defaultValues={defaultFormValues}
          errors={errors}
        />
      )}
    </NewInviteContext.Provider>
  );
}
